import React from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import get from 'lodash/get';
import Icon from '@xo-union/tk-component-icons';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { Button } from '@xo-union/tk-component-buttons';
import GdsUiState from '~/glm/models/gds/gds-ui-state';
import BulkDeleteModal from '~/glm/components/guest-list/modals/bulk-delete-modal';
import GuestListStore from '~/glm/models/guest-list-store';
import AnalyticsStore from '~/glm/models/analytics-store';
import GuestCollection from '~/glm/models/gds/gds-guest-collection';
import styles from './bulk-select-toolbar.scss';

@inject('gdsUiState', 'guests', 'guestListStore', 'analyticsStore')
@observer
class BulkSelectToolbar extends React.Component {
  static propTypes = {
    gdsUiState: PropTypes.instanceOf(GdsUiState).isRequired,
    guests: PropTypes.instanceOf(GuestCollection).isRequired,
    guestListStore: PropTypes.instanceOf(GuestListStore).isRequired,
    analyticsStore: PropTypes.instanceOf(AnalyticsStore).isRequired,
    dropdownComponent: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      bulkDelete: false,
      numberOfGuestsDeleted: 0,
    };

    this.rsvpFilterValueMap = {
      Attending: true,
      Declined: false,
      Invited: null,
    };
  }

  get updatedGuestsToBeDeleted() {
    const { gdsUiState } = this.props;
    const { selectedGuests, rsvpBulkSelectedEvent } = gdsUiState;

    return selectedGuests.filter(guest => {
      const { invitations } = guest;

      return guest.invitations.length === 1 && invitations[0].eventId === get(rsvpBulkSelectedEvent, 'id');
    });
  }

  shouldClearSelection(type, value, event) {
    const { gdsUiState } = this.props;
    const { rsvpBulkSelection, rsvpBulkSelectedEvent } = gdsUiState;
    const rsvpSelection = type !== 'meal';
    const matchesExistingRsvpValue = rsvpBulkSelection === value;
    const newRsvpEvent = event && event !== rsvpBulkSelectedEvent;

    return rsvpSelection && matchesExistingRsvpValue && !newRsvpEvent;
  }

  setDeletingGuests = () => {
    this.setState({ bulkDelete: true });

    const { gdsUiState, analyticsStore } = this.props;
    const action = gdsUiState.allGuestsSelected ? 'delete all guests' : 'delete guests';

    analyticsStore.track('Guest Deleted', {
      action,
      source: 'bulk delete',
      guestToDeleteCount: gdsUiState.numberOfGuestsSelected,
    });
  };

  closeDeleteGuestsModal = () => {
    this.setState({ bulkDelete: false });
  };

  closeModal = type => {
    if (type === 'delete') {
      this.setState({ bulkDelete: false });
    }
  };

  deleteGuests = () => {
    const { guests, gdsUiState, guestListStore, analyticsStore } = this.props;
    this.setState({ numberOfGuestsDeleted: gdsUiState.numberOfGuestsSelected });
    guests.deleteGuests(gdsUiState.selectedGuests).then(() => {
      guestListStore.updateHousehold();
    });
    gdsUiState.triggerBulkDeleteConfirmation();
    this.closeDeleteGuestsModal();

    for (let i = gdsUiState.numberOfGuestsSelected - 1; i >= 0; i -= 1) {
      const trackingParams = { source: 'bulk delete' };
      if (gdsUiState.selectedGuests[i].country) {
        Object.assign(trackingParams, {
          guestCountryCode: gdsUiState.selectedGuests[i].country,
        });
      }
      analyticsStore.track('Guest Deleted', trackingParams);
    }
  };

  setSelectedStatus = (type, value, event) => {
    const { gdsUiState } = this.props;
    const { mealBulkSelection } = gdsUiState;

    if (this.shouldClearSelection(type, value, event)) {
      gdsUiState.setRsvpBulkSelection(null, null);
    } else if (type === 'meal' && mealBulkSelection === value) {
      gdsUiState.setMealBulkSelection(null);
    } else if (type === 'rsvp' || type === 'overview') {
      gdsUiState.setRsvpBulkSelection(value, event);
    } else if (type === 'meal') {
      gdsUiState.setMealBulkSelection(value);
    }
  };

  updateSelectedGuests = () => {
    const { gdsUiState } = this.props;
    const { selectedGuests, selectedEvent } = gdsUiState;

    selectedGuests.forEach(guest => {
      if (selectedEvent) {
        this.updateSelectedEventInvitation(guest);
      } else {
        this.updateInvitation(guest);
      }
    });
  };

  updateSelectedGuestsModal = () => {
    this.updateSelectedGuests();
    this.closeModal('update');
  };

  updateInvitation = guest => {
    const { gdsUiState } = this.props;
    const { rsvpBulkSelectedEvent, rsvpBulkSelection } = gdsUiState;
    const rsvpBulkSelectedEventId = get(rsvpBulkSelectedEvent, 'id');

    const invitation = guest.getInvitationByEvent(rsvpBulkSelectedEventId);

    if (invitation && rsvpBulkSelection === 'Not Invited') {
      guest.removeInvitation(invitation);
      invitation.delete();
    } else if (invitation) {
      let apiParams = { ...invitation };

      apiParams.rsvp = this.rsvpFilterValueMap[rsvpBulkSelection];
      invitation.updateInvite(apiParams);
      invitation.put(invitation.apiParams);
    } else if (!invitation && rsvpBulkSelection !== 'Not Invited') {
      const newInvitation = {
        rsvp: this.rsvpFilterValueMap[rsvpBulkSelection],
        eventId: rsvpBulkSelectedEventId,
      };

      guest.addInvitation(newInvitation, guest.householdId, guest.id);
      guest.getInvitationByEvent(rsvpBulkSelectedEventId).post();
    }
  };

  updateMealAnswer = (apiParams, mealBulkSelection) => {
    const { gdsUiState } = this.props;
    const mealQuestionId = get(gdsUiState, ['selectedEvent', 'mealQuestion', 'id']);
    const mealAnswer = apiParams.answers.find(answer => answer.questionId === mealQuestionId);

    if (!mealAnswer && mealBulkSelection.text !== null) {
      apiParams.answers = [
        ...apiParams.answers,
        {
          questionId: mealQuestionId,
          optionId: mealBulkSelection.id,
        },
      ];
    } else if (mealAnswer && mealBulkSelection.text === null) {
      apiParams.answers = apiParams.answers.filter(answer => answer !== mealAnswer);
    } else if (mealAnswer && mealBulkSelection.id !== mealAnswer.optionId) {
      apiParams.answers = apiParams.answers.map(answer =>
        answer === mealAnswer
          ? {
              questionId: mealQuestionId,
              optionId: mealBulkSelection.id,
            }
          : answer
      );
    }

    return apiParams;
  };

  updateSelectedEventInvitation = guest => {
    const { gdsUiState } = this.props;
    const { rsvpBulkSelection, mealBulkSelection, selectedEvent } = gdsUiState;
    const selectedEventId = get(selectedEvent, 'id');
    const invitation = guest.getInvitationByEvent(selectedEventId);

    let apiParams = { ...invitation };
    if (rsvpBulkSelection) {
      apiParams.rsvp = this.rsvpFilterValueMap[rsvpBulkSelection];
    }
    if (mealBulkSelection) {
      apiParams = this.updateMealAnswer(apiParams, mealBulkSelection);
    }

    invitation.updateInvite(apiParams);
    invitation.put(invitation.apiParams);
  };

  render() {
    const { gdsUiState, dropdownComponent } = this.props;

    return (
      <div className={styles.bulkSelectToolbar}>
        {gdsUiState.showBulkDeleteConfirmation && !gdsUiState.numberOfGuestsSelected && (
          <span className={styles.deleteConfirmation}>
            All set! {this.state.numberOfGuestsDeleted} Guest(s) Deleted
            <Icon name="checkmark" className={styles.checkmark} />
          </span>
        )}
        {gdsUiState.numberOfGuestsSelected > 0 && (
          <React.Fragment>
            <div className={styles.selectedGuestContainer}>
              <div className={styles.selectedGuests}>{gdsUiState.numberOfGuestsSelected} SELECTED</div>
              {dropdownComponent && dropdownComponent(this.setSelectedStatus)}
              <Button onClick={this.updateSelectedGuests}>Apply To Guests</Button>
              <DisplayButton className={styles.deleteLink} onClick={this.setDeletingGuests} iconName="delete">
                Delete selected guests
              </DisplayButton>
            </div>
            {this.state.bulkDelete && (
              <BulkDeleteModal
                onAccept={this.deleteGuests}
                onClose={() => this.closeModal('delete')}
                numberOfGuestsDeleted={gdsUiState.numberOfGuestsSelected}
              />
            )}
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default BulkSelectToolbar;
